import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { OrganizationInvitationErrorCodes } from '@common/neo.tax';
import { Loading, Stack, Typography } from '@ui-components/neo.tax';
import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import z from 'zod';

import { useWakeBackend } from '@neo.tax/onboarding/hooks';
import { AuthFormHeader, StyledSplitScreen, SubmitButton, TestimonialMemo } from '@neo.tax/onboarding/organisms';
import {
  AuthenticationState,
  Path,
  clearFirebase,
  getInvitationLoginPath,
  getPath,
  getValidSsoProviderId,
  useCurrentUser,
  useUrlQueryParam,
} from '@neo.tax/shared';
import { ACCEPT_INVITATION, GET_USER } from '@neo.tax/shared/graphql/documents.graphql';
import {
  AcceptInvitationMutation,
  AcceptInvitationMutationVariables,
  GetUserQuery,
  GetUserQueryVariables,
} from '@neo.tax/shared/graphql/types';
import { refreshFirebaseIdToken } from '@neo.tax/shared/third-parties/firebase/firebase.utils';

type AcceptState = 'idle' | 'accepting' | 'success' | 'error';

// Mirrors the parser used in VerifyEmailSent.tsx so we can extract an invitation-specific
// error code out of Apollo's graphQLErrors without any casts.
const apolloErrorShape = z.object({
  graphQLErrors: z
    .array(
      z.object({
        extensions: z
          .object({
            errors: z.array(z.object({ code: z.string() })).optional(),
          })
          .optional(),
      }),
    )
    .optional(),
});

const getInvitationErrorCode = (error: unknown): string | undefined => {
  const parsed = apolloErrorShape.safeParse(error);
  if (!parsed.success) {
    return undefined;
  }
  return parsed.data.graphQLErrors?.[0]?.extensions?.errors?.[0]?.code;
};

const INVALID_COPY = {
  title: 'This invitation is no longer valid',
  description:
    'The link may have expired or been revoked. Please contact your administrator to request a new invitation.',
};

const MISMATCH_COPY = {
  title: 'This invitation is not for your account',
  description: 'Please sign in with the email address the invitation was sent to.',
};

const SSO_COPY = {
  title: "Can't accept this invitation",
  description: 'You must sign in with the enterprise SSO provider configured for this organization.',
};

const ALREADY_IN_ORG_COPY = {
  title: 'You already belong to an organization',
  description:
    'You can only be a member of one organization at a time. Please contact Neo.Tax Support (support@neo.tax) if you need help switching organizations.',
};

export const AcceptInvitation = (): React.ReactElement => {
  useWakeBackend();
  const token = useUrlQueryParam('token');
  // Carried from the invite link; threaded onto the signup/sign-in routes so those forms can
  // pre-populate the invited email. Prefill only — acceptance still enforces the email match.
  const prefillEmail = useUrlQueryParam('prefillEmail');
  const ssoProviderId = getValidSsoProviderId(useUrlQueryParam('ssoProviderId'));
  const { loading: userLoading, user } = useCurrentUser();
  const { authenticationServiceState } = useReactiveVar(AuthenticationState.authenticationStateVar);
  const isLoggedIn = !!authenticationServiceState.authenticationUser;
  const location = useLocation();

  const [acceptInvitation] = useMutation<AcceptInvitationMutation, AcceptInvitationMutationVariables>(
    ACCEPT_INVITATION,
  );
  const [fetchUser] = useLazyQuery<GetUserQuery, GetUserQueryVariables>(GET_USER, { fetchPolicy: 'network-only' });
  const [status, setStatus] = useState<AcceptState>('idle');
  const [errorCode, setErrorCode] = useState<string | undefined>(undefined);
  const [isSwitchingToSso, setIsSwitchingToSso] = useState(false);
  const navigate = useNavigate();

  // React 18 strict-mode invokes this effect twice in dev; guard with a ref so we only
  // call the mutation once per mount (it's one-shot: nulls the tokenHash on success).
  const hasAttemptedRef = useRef(false);

  const userId = user?.id;

  // Strip a self-referential `?continueUrl=/accept-invitation…` from the URL. The param
  // only matters on auth flow pages (login / verify-email-sent / profile) that forward
  // users onward after completing; once we've actually landed on /accept-invitation the
  // param is redundant and risks a loop if the user later navigates elsewhere and a guard
  // picks it back up.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const continueUrlParam = params.get('continueUrl');
    if (continueUrlParam && continueUrlParam.startsWith('/accept-invitation')) {
      params.delete('continueUrl');
      const newSearch = params.toString();
      navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  const authEmailVerified = authenticationServiceState.authenticationUser?.emailVerified ?? false;

  useEffect(() => {
    if (!token || !isLoggedIn || hasAttemptedRef.current) {
      return;
    }
    // A verified session must wait for the current user to finish loading before accepting. The
    // post-accept GET_USER refetch below is keyed on userId, and useProfileRedirect bounces a user
    // whose cached organization is still null to /profile ("Let's Get Started"). Accepting while
    // GET_USER is in flight (userId undefined) skips that refetch and leaves a pre-accept, org-less
    // user in the Apollo cache — stranding the invitee on the profile page instead of /companies.
    // Unverified invitees are intentionally exempt: their GET_USER is email-verified-gated on the
    // backend and never resolves until acceptance verifies them, so waiting on userLoading would
    // deadlock the page on a spinner. They have no cached user to go stale, so /companies loads fresh.
    if (authEmailVerified && userLoading) {
      return;
    }
    hasAttemptedRef.current = true;
    setStatus('accepting');
    (async (): Promise<void> => {
      try {
        // Force-refresh the Firebase ID token first. Handles the race where a user clicks
        // the invite link before completing email verification on a different device —
        // Firebase's server-side `email_verified` may be fresh but the cached token still
        // carries the old claim, which the backend auth guard would reject with
        // EMAIL_NOT_VERIFIED. Errors are swallowed: if the refresh genuinely fails the
        // mutation below still runs and surfaces a clearer error downstream.
        try {
          await refreshFirebaseIdToken();
        } catch {
          // no-op
        }
        const { data } = await acceptInvitation({ variables: { acceptInvitationInput: { token } } });
        // Acceptance verifies the invitee's email server-side. Refresh the Firebase user so the
        // now-verified emailVerified claim reaches the cached ID token BEFORE we read the user —
        // GET_USER is email-verified-gated, so reading with the stale unverified token bounces the
        // page. (Same emailVerified-propagation issue OPS-772 fixed for SSO; harmless for an
        // already-verified invitee.)
        try {
          await refreshFirebaseIdToken();
        } catch {
          // no-op
        }
        // Refetch GET_USER so useAuthRedirect on /companies sees the new org scope and
        // allowRealCompanyDatetime instead of the stale cache. An unverified invitee has no loaded
        // user yet (userId undefined), so this is skipped and /companies loads fresh.
        if (userId) {
          await fetchUser({ variables: { userId } });
        }
        setStatus('success');
        const organizationId = data?.acceptInvitation.organizationId;
        navigate(organizationId ? '/companies' : getPath({ path: Path.PROFILE }));
      } catch (error) {
        setErrorCode(getInvitationErrorCode(error));
        setStatus('error');
      }
    })();
  }, [token, isLoggedIn, authEmailVerified, userLoading, userId, acceptInvitation, fetchUser, navigate]);

  const prefillEmailParam = prefillEmail ? `&prefillEmail=${encodeURIComponent(prefillEmail)}` : '';
  const enterpriseSsoHref =
    token && ssoProviderId
      ? `${getPath({ path: Path.ENTERPRISE_SSO })}?inviteToken=${encodeURIComponent(token)}${prefillEmailParam}&ssoProviderId=${encodeURIComponent(ssoProviderId)}`
      : null;

  // Waits for the sign-out to land, so the SSO page is reached signed out.
  useEffect(() => {
    if (isSwitchingToSso && !isLoggedIn && enterpriseSsoHref) {
      navigate(enterpriseSsoHref);
    }
  }, [isSwitchingToSso, isLoggedIn, enterpriseSsoHref, navigate]);

  // No token → send the user home. Nothing actionable on this page without one.
  if (!token) {
    return <Navigate to="/" replace />;
  }

  if (userLoading || status === 'accepting' || (isLoggedIn && status === 'idle')) {
    return <Loading height={80} />;
  }

  // Logged-in errors map to dedicated copy; everything else falls through to the generic
  // "no longer valid" message. Acceptance was already attempted at this point.
  if (status === 'error') {
    let copy = INVALID_COPY;
    if (errorCode === OrganizationInvitationErrorCodes.EMAIL_MISMATCH) copy = MISMATCH_COPY;
    else if (errorCode === OrganizationInvitationErrorCodes.SSO_PROVIDER_MISMATCH) copy = SSO_COPY;
    else if (errorCode === OrganizationInvitationErrorCodes.INVITEE_ALREADY_IN_ORGANIZATION) copy = ALREADY_IN_ORG_COPY;
    return (
      <StyledSplitScreen
        leftSide={
          <AuthFormHeader
            pageChangeText="Have an account?"
            pageChangeLinkText="Sign in"
            pageChangeTo="/login"
            title={copy.title}
            description={copy.description}
            form={
              <Stack direction="column" paddingY={6}>
                {errorCode === OrganizationInvitationErrorCodes.SSO_PROVIDER_MISMATCH && enterpriseSsoHref && (
                  <SubmitButton
                    type="button"
                    variant="primary"
                    rounded="regular"
                    size="base"
                    disabled={isSwitchingToSso}
                    isLoading={isSwitchingToSso}
                    onClick={() => {
                      setIsSwitchingToSso(true);
                      clearFirebase();
                    }}
                  >
                    Sign out and continue with SSO
                  </SubmitButton>
                )}
              </Stack>
            }
          />
        }
        rightSide={<TestimonialMemo />}
      />
    );
  }

  // Logged out + token present → preview with signup and signin routes that thread the
  // token (and the invited email for prefill) so they survive the auth round-trip.
  const signupHref = `/signup?inviteToken=${encodeURIComponent(token)}${prefillEmailParam}`;

  return (
    <StyledSplitScreen
      leftSide={
        <AuthFormHeader
          pageChangeText={enterpriseSsoHref ? 'use' : 'Already have an account?'}
          pageChangeLinkText={enterpriseSsoHref ? 'another sign-in method' : 'Sign in'}
          pageChangeTo={getInvitationLoginPath({ token, prefillEmail, ssoProviderId })}
          title="You've been invited to Neo.Tax"
          description={
            enterpriseSsoHref
              ? "Sign in with your organization's SSO provider to accept this invitation and join your organization."
              : 'Sign up or sign in to accept this invitation and join your organization.'
          }
          form={
            <Stack direction="column" paddingY={6} gap={2}>
              <SubmitButton
                type="button"
                variant="primary"
                rounded="regular"
                size="base"
                onClick={() => navigate(enterpriseSsoHref ?? signupHref)}
              >
                {enterpriseSsoHref ? 'Continue with SSO' : 'Sign up'}
              </SubmitButton>
              <Typography as="span" fontSize="sm">
                Only the invited email address can accept this invitation.
              </Typography>
              {user && null}
            </Stack>
          }
        />
      }
      rightSide={<TestimonialMemo />}
    />
  );
};
